import { useEffect, useRef } from "react";

/**
 * Subscribe to the field SSE stream (/api/field/events).
 *
 * The server only sends events targeted at this field operator (filtered server-side
 * via `target_user_ids`). The hook invokes `onEvent({type, ...})` for each.
 *
 * Auto-reconnects with exponential backoff if the connection drops.
 *
 * Props:
 *   onEvent — function called for every event payload
 *   enabled — boolean, default true; pass false to pause subscription
 *   tokenKey — sessionStorage key holding the field JWT (default 'gow_field_token')
 */
export default function useFieldEvents(onEvent, { enabled = true, tokenKey = "gow_field_token" } = {}) {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;
    const token = sessionStorage.getItem(tokenKey);
    if (!token) return;

    const base = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${base}/api/field/events?token=${encodeURIComponent(token)}`;

    let es = null;
    let closed = false;
    let retryMs = 1000;
    let retryTimer = null;

    const handleEvent = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        handlerRef.current?.(data);
      } catch (err) {
        console.warn("[field-sse] bad event payload:", err?.message);
      }
    };

    const connect = () => {
      if (closed) return;
      try {
        es = new EventSource(url);
        es.addEventListener("hello", () => { retryMs = 1000; });
        // Field-relevant event types
        ["so_assigned", "so_updated", "so_reordered", "so_facade_updated",
         "so_started", "so_finished", "so_photo_added", "message"].forEach((type) => {
          es.addEventListener(type, handleEvent);
        });
        es.onerror = () => {
          if (closed) return;
          try { es.close(); } catch (_) { /* ignore */ }
          retryTimer = setTimeout(connect, retryMs);
          retryMs = Math.min(retryMs * 2, 30000);
        };
      } catch (err) {
        console.warn("[field-sse] connect failed:", err?.message);
        retryTimer = setTimeout(connect, retryMs);
      }
    };
    connect();

    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (es) try { es.close(); } catch (_) { /* ignore */ }
    };
  }, [enabled, tokenKey]);
}
